import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Upload, CloudUpload, X, Check, Building2, MapPin, Contact, ChevronRight } from "lucide-react";
import OrganizationClient, { OrganizationProfile } from "../data/api/OrganizationClient";
import Loader from "../../../shared/components/Loader";
import { CustomDropdown } from "../../../shared/components/CustomDropdown";
import "../organizations.css";
import "../../interviewSchedule/styles/ScheduleStyles.css";

interface FormErrors {
  name?: string;
  email?: string;
}

const OrganizationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [oldS3Key, setOldS3Key] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const fetchOrganization = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const org = await OrganizationClient.getOrganization(id);
        if (cancelled) return;
        setName(org.name || "");
        setEmail(org.email || "");
        setLocation(org.location || "");
        setIndustry(org.industry || "");
        setContactName(org.contact?.name || "");
        setContactPhone(org.contact?.phone || "");
        setContactEmail(org.contact?.email || "");
        setOldS3Key(org.logo_s3_key || null);
        setLogoPreview(org.logo_url || null);
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err.response?.data?.detail || err.message || "Failed to load organization");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOrganization();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const validateStep = (currentStep: number): boolean => {
    const newErrors: FormErrors = {};
    if (currentStep === 1) {
      if (!name.trim()) newErrors.name = "Name is required";
      if (!email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate = (): boolean => {
    return validateStep(1); // Profile step has the required fields
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 2));
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNext();
    } else {
      submitApiCall();
    }
  };

  const submitApiCall = async () => {
    setSubmitError(null);
    if (!validate()) return;

    setSaving(true);
    try {
      const payload: OrganizationProfile = {
        name: name.trim(),
        email: email.trim(),
        location: location.trim() || null,
        industry: industry.trim() || null,
        company_name: name.trim() || null,
        contact: {
          name: contactName.trim() || null,
          phone: contactPhone.trim() || null,
          email: contactEmail.trim() || null,
        },
      };

      let updatedOrgId = id;
      if (isEditing) {
        const updated = await OrganizationClient.updateOrganization(id!, payload);
        updatedOrgId = updated.organization_id;
      } else {
        const created = await OrganizationClient.createOrganization(payload);
        updatedOrgId = created.organization_id;
      }

      if (logoFile && updatedOrgId) {
        await OrganizationClient.uploadLogo(updatedOrgId, logoFile, oldS3Key);
      }
      navigate(`/dashboard/organizations`, { state: { selectedOrgId: updatedOrgId } });
    } catch (err: any) {
      setSubmitError(err.response?.data?.detail || err.message || "Failed to save organization");
      setSaving(false);
    }
  };

  if (loadError && !loading) {
    return (
      <motion.div
        className="learner-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div className="org-detail-card">
          <div
            className="form-error"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: 12 }}
          >
            {loadError}
          </div>
          <button className="org-btn" style={{ marginTop: 16 }} onClick={() => navigate("/dashboard/organizations")}>
            <ArrowLeft size={16} />
            Back to Organizations
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="is-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading || saving} message={saving ? "Saving organization..." : "Loading organization..."} />

      {/* ── Hero ── */}
      <div className="lp-hero" style={{ marginBottom: "24px" }}>
        <div className="lp-hero-left" style={{ alignItems: "flex-start" }}>
          <button
            className="btn-ghost is-back-btn"
            onClick={() => navigate("/dashboard/organizations", { state: { selectedOrgId: id } })}
            style={{ padding: "8px", height: "auto", borderRadius: "10px" }}
          >
            <ArrowLeft size={20} />
          </button>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "16px",
              paddingTop: "2px",
            }}
          >
            <h1 style={{ margin: 0 }}>
              {isEditing ? "Edit Organization" : "Create Organization"}
            </h1>
            <span className="lp-hero-chip" style={{ display: "inline-flex" }}>
              {isEditing ? "Update organization details" : "Create a new organization"}
            </span>
          </div>
        </div>
      </div>

      <div className="is-split-layout">
        {/* Left Side: Vertical Stepper */}
        <div className="is-sidebar-steps">
          <div
            className={`is-sidebar-step ${step === 1 ? "active" : step > 1 ? "completed" : ""}`}
            onClick={() => setStep(1)}
            style={{ cursor: "pointer" }}
          >
            <div className="is-step-icon">
              {step > 1 ? <Check size={16} /> : 1}
            </div>
            <div className="is-step-text">
              <strong>Basic Info</strong>
              <span>Configure basic info</span>
            </div>
          </div>
          <div className="is-step-connector" />
          <div
            className={`is-sidebar-step ${step === 2 ? "active" : step > 2 ? "completed" : ""}`}
            onClick={() => {
              if (validateStep(1)) setStep(2);
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="is-step-icon">
              {step > 2 ? <Check size={16} /> : 2}
            </div>
            <div className="is-step-text">
              <strong>Contact Details</strong>
              <span>Assign contact person</span>
            </div>
          </div>
        </div>

        {/* Right Side: Main Content */}
        <div className="is-main-content">
          <form onSubmit={(e) => e.preventDefault()} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {submitError && (
              <div className="form-error" style={{ marginBottom: 24 }}>
                {submitError}
              </div>
            )}

            <div className="is-form-scrollable" style={{ paddingRight: "8px", paddingBottom: "24px", display: "flex", flexDirection: "column", gap: "20px", flex: 1 }}>
              
              {step === 1 && (
                <div className="is-form-card" style={{ flex: "none", height: "auto", minHeight: 0 }}>
                  <div className="is-human-panel-header" style={{ marginBottom: "16px", borderBottom: "none", paddingBottom: 0 }}>
                    <div className="is-human-panel-icon" style={{ background: "#F3EBFC", color: "#7A3FC2" }}>
                      <Building2 size={16} />
                    </div>
                    <h3 style={{ margin: 0 }}>Basic Profile</h3>
                  </div>

                  <div className="is-form-row" style={{ marginBottom: "24px" }}>
                    <div className="is-form-group full-width">
                      <label>Organization Logo</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
                        {logoPreview ? (
                          <div style={{ position: "relative", width: "88px", height: "88px", borderRadius: "50%", overflow: "hidden", border: "2px solid #E5E7EB", background: "#fff", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
                            <img src={logoPreview} alt="Logo preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button
                              type="button"
                              onClick={() => {
                                setLogoPreview(null);
                                setLogoFile(null);
                              }}
                              style={{
                                position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: 0, transition: "opacity 0.2s"
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
                            >
                              <X size={24} strokeWidth={2} />
                            </button>
                          </div>
                        ) : (
                          <div 
                            style={{ 
                              width: "88px", height: "88px", borderRadius: "50%", background: "linear-gradient(135deg, #7A3FC2, #4F46E5)", 
                              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "32px", fontWeight: "bold",
                              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)", border: "2px solid #fff"
                            }}
                          >
                            {name.trim() ? (name.trim().split(' ').length > 1 ? name.trim().split(' ')[0][0] + name.trim().split(' ')[1][0] : name.trim().substring(0, 2)).toUpperCase() : "JH"}
                          </div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: "none" }} 
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                const file = e.target.files[0];
                                setLogoFile(file);
                                setLogoPreview(URL.createObjectURL(file));
                              }
                            }}
                          />
                          <button 
                            type="button" 
                            onClick={() => fileInputRef.current?.click()}
                            style={{ 
                              padding: "8px 18px", 
                              fontSize: "14px", 
                              fontWeight: 600, 
                              border: "1px solid rgba(11, 87, 208, 0.2)", 
                              borderRadius: "12px", 
                              background: "rgba(11, 87, 208, 0.04)",
                              backdropFilter: "blur(8px)",
                              color: "#003b73",
                              width: "fit-content", 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "8px",
                              cursor: "pointer",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "rgba(11, 87, 208, 0.08)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "rgba(11, 87, 208, 0.04)";
                            }}
                          >
                            <CloudUpload size={18} strokeWidth={2.2} /> Upload Logo
                          </button>
                          <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                            Recommended size: 256x256px. Max 2MB.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Company Name <span className="org-required" style={{ color: "#EF4444" }}>*</span></label>
                      <input
                        type="text"
                        className={errors.name ? "error" : ""}
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                        }}
                        placeholder="Company name"
                      />
                      {errors.name && <span className="org-form-error" style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px" }}>{errors.name}</span>}
                    </div>
                    <div className="is-form-group">
                      <label>Email <span className="org-required" style={{ color: "#EF4444" }}>*</span></label>
                      <input
                        type="email"
                        className={errors.email ? "error" : ""}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                        }}
                        placeholder="contact@company.com"
                      />
                      {errors.email && <span className="org-form-error" style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px" }}>{errors.email}</span>}
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City, Country"
                      />
                    </div>
                    <div className="is-form-group">
                      <label>Industry</label>
                      <CustomDropdown
                        options={[
                          { value: "Technology", label: "Technology" },
                          { value: "Healthcare", label: "Healthcare" },
                          { value: "Finance", label: "Finance" },
                          { value: "Retail", label: "Retail" },
                          { value: "Education", label: "Education" },
                          { value: "Manufacturing", label: "Manufacturing" },
                          { value: "Consulting", label: "Consulting" },
                          { value: "Other", label: "Other" }
                        ]}
                        value={industry}
                        onChange={(val) => setIndustry(val)}
                        placeholder="Select Industry"
                        placement="top"
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="is-form-card" style={{ flex: "none", height: "auto", minHeight: 0 }}>
                  <div className="is-human-panel-header" style={{ marginBottom: "16px", borderBottom: "none", paddingBottom: 0 }}>
                    <div className="is-human-panel-icon" style={{ background: "#DCFCE7", color: "#16A34A" }}>
                      <Contact size={16} />
                    </div>
                    <h3 style={{ margin: 0 }}>Contact Details</h3>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Contact Name</label>
                      <input
                        type="text"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="Contact person name"
                      />
                    </div>
                    <div className="is-form-group" style={{ visibility: "hidden" }}></div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label>Contact Phone</label>
                      <input
                        type="text"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <div className="is-form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="direct@organization.com"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "auto", display: "flex", gap: "12px", paddingTop: "16px", borderTop: "1px solid var(--border)" }}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  if (step > 1) {
                    handleBack();
                  } else {
                    navigate("/dashboard/organizations", { state: { selectedOrgId: id } });
                  }
                }}
                disabled={saving}
                style={{ boxSizing: "border-box", height: "40px", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "9999px", fontSize: "15px", fontWeight: 500, color: "#1F2937", background: "#fff", border: "1px solid #E5E7EB", cursor: "pointer" }}
              >
                {step > 1 ? "Back" : "Cancel"}
              </button>
              {step < 2 ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleNext}
                  style={{ boxSizing: "border-box", height: "40px", padding: "0 28px", borderRadius: "9999px", fontSize: "15px", fontWeight: 600, background: "#047857", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "1px solid transparent", cursor: "pointer" }}
                >
                  Next <ChevronRight size={18} />
                </button>
              ) : (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={submitApiCall}
                  disabled={saving}
                  style={{ boxSizing: "border-box", height: "40px", padding: "0 28px", borderRadius: "9999px", fontSize: "15px", fontWeight: 600, background: "#047857", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", border: "1px solid transparent", cursor: "pointer", boxShadow: "0 4px 12px rgba(4, 120, 87, 0.2)" }}
                >
                  {saving ? "Saving..." : (
                    <>
                      <Save size={18} />
                      {isEditing ? "Save Changes" : "Create"}
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
};

export default OrganizationFormPage;